import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, Section, useToast, type Requirement } from "@/components/ui";
import { CustomNsNotice } from "@/components/domains/parts";
import { domainsApi, type DnsPreview, type DnsTemplateKind, type Domain, type Zone } from "@/lib/domains";

import { BatchReviewModal } from "./BatchReviewModal";

const TEMPLATES: { kind: DnsTemplateKind; label: string; detail: string }[] = [
  {
    kind: "google_workspace",
    label: "Google Workspace",
    detail: "Mail (MX) and sender-verification (SPF) records for Google's business email.",
  },
  {
    kind: "microsoft365",
    label: "Microsoft 365",
    detail: "Mail (MX), sender-verification (SPF) and enrollment records for Microsoft's business email.",
  },
];

/**
 * One-step DNS templates (PHP-3942, AC9): Google Workspace / Microsoft 365's standard mail records,
 * through the same preview-then-apply flow as a manual DNS change (AC12).
 */
export function TemplatesSection({
  domain,
  managed,
  editable,
  editRequires,
  onApplied,
}: {
  domain: Domain;
  /** Whether CortexGrip hosts this domain's DNS zone at all (templates need it, AC13). */
  managed: boolean;
  editable: boolean;
  editRequires: Requirement;
  onApplied: (next: Zone) => void | Promise<void>;
}) {
  const { orgId } = useOrganization();
  const toast = useToast();
  const [stage, setStage] = useState<{ kind: DnsTemplateKind; label: string; preview: DnsPreview } | null>(
    null,
  );

  const preview = useMutation({
    mutationFn: ({ kind }: { kind: DnsTemplateKind; label: string }) =>
      domainsApi.previewTemplate(orgId, domain.id, kind),
    onSuccess: (result, { kind, label }) => setStage({ kind, label, preview: result }),
  });
  const apply = useMutation({
    mutationFn: (kind: DnsTemplateKind) => domainsApi.applyTemplate(orgId, domain.id, kind),
    onSuccess: async (next) => {
      await onApplied(next);
      toast.show({ tone: "success", title: "Template applied" });
      setStage(null);
    },
  });

  if (!managed) {
    return (
      <Section title="Quick setup" subtitle="Apply a provider's standard DNS records in one step">
        <CustomNsNotice />
      </Section>
    );
  }

  return (
    <Section title="Quick setup" subtitle="Apply a provider's standard DNS records in one step">
      <div className="flex flex-wrap gap-3">
        {TEMPLATES.map((t) => (
          <div
            key={t.kind}
            className="flex min-w-[220px] flex-1 flex-col gap-2 rounded-xl border border-border p-4"
          >
            <p className="text-sm font-semibold text-fg">{t.label}</p>
            <p className="text-xs text-fg-subtle">{t.detail}</p>
            {editable && (
              <Button
                size="sm"
                variant="secondary"
                requires={editRequires}
                loading={preview.isPending && preview.variables?.kind === t.kind}
                onClick={() => {
                  preview.reset();
                  preview.mutate(t);
                }}
              >
                Apply {t.label}
              </Button>
            )}
          </div>
        ))}
      </div>
      {preview.isError && <ErrorAlert error={preview.error} />}
      {stage && (
        <BatchReviewModal
          open
          fqdn={domain.fqdn}
          title={`Apply the ${stage.label} template?`}
          description={`This sets up ${domain.fqdn}'s mail records for ${stage.label}.`}
          preview={stage.preview}
          onClose={() => {
            if (apply.isPending) return;
            setStage(null);
            apply.reset();
          }}
          onConfirm={() => apply.mutate(stage.kind)}
          confirmLabel="Apply template"
          applying={apply.isPending}
          error={apply.error}
        />
      )}
    </Section>
  );
}
